import { type ReactNode } from "react";
import { twMerge } from "tailwind-merge";

import FormBuilderActionButton, {
  type FormBuilderActions,
} from "containers/admin/clients/client/components/form-builder/form-builder-action-button";
import FormBuilderFieldDisplay from "containers/admin/clients/client/components/form-builder/form-builder-field-display";
import FormBuilderKeyValueTable from "containers/admin/clients/client/components/form-builder/form-builder-key-value-table";
import FormBuilderUploadPreview from "containers/admin/clients/client/components/form-builder/form-builder-upload-preview";
import FormBuilderViewerEmptyState from "containers/admin/clients/client/components/form-builder/form-builder-viewer-empty-state";
import FormBuilderViewerDynamicSection from "containers/admin/clients/client/components/form-builder/form-builder-viewer-dynamic-section";
import FormBuilderViewerFlatDynamicSection from "containers/admin/clients/client/components/form-builder/form-builder-viewer-flat-dynamic-section";
import { renderGroupedPropertyEntries } from "containers/admin/clients/client/components/form-builder/form-builder-grouped-fields";
import { renderSectionHeading } from "containers/admin/clients/client/components/form-builder/form-builder-section-heading";
import {
  getNestedValueByPath,
  getObjectHeadingLevel,
  isObjectSchema,
  isRecord,
} from "containers/admin/clients/client/components/form-builder/form-builder-utils";
import {
  formatFieldDisplayValueForReadOnly,
  hasFieldDisplayContent,
  hasFieldDisplayLabel,
  isConditionalTriggerField,
  isDynamicSectionField,
  isFlatDynamicSectionItemSchema,
  isKeyValueTableField,
  shouldShowReadOnlyEmptyState,
} from "containers/admin/clients/client/components/form-builder/form-builder-viewer-utils";
import {
  getConditionalDependentFieldNames,
  getConditionalRules,
  getDynamicSectionConfig,
  getDynamicSectionHeaderFieldNames,
  getFieldTitle,
  getRowGroups,
  getSectionLayout,
  getUploadConfig,
  isActionButtonField,
  isDisplayedAsLink,
  isFullWidthField,
  isUploadField,
  matchesConditionalShowIf,
  sortJsonFormFields,
  type JsonFormObjectSchema,
  type JsonFormSchema,
} from "containers/admin/clients/client/components/form-builder/form-schema";

type CreateFormBuilderViewerFieldsParams = {
  formData: Record<string, unknown>;
  actions?: FormBuilderActions;
};

const compactNodes = (nodes: ReactNode[]): ReactNode[] =>
  nodes.filter((node) => node !== null && node !== undefined && node !== false);

export const createFormBuilderViewerFields = ({
  formData,
  actions,
}: CreateFormBuilderViewerFieldsParams) => {
  let useKeyValueTableForScalars = false;

  const getRowGroupTableEntries = (
    groupEntries: [string, JsonFormSchema][],
    parentSchema: JsonFormObjectSchema,
  ): [string, JsonFormSchema][] =>
    groupEntries.filter(
      ([groupName, groupField]) =>
        isKeyValueTableField(groupField) &&
        hasFieldDisplayLabel(groupName, groupField) &&
        !isConditionalTriggerField(groupName, parentSchema),
    );

  const renderConditionalFields = (
    name: string,
    objectSchema: JsonFormObjectSchema,
    prefix = "",
  ) =>
    getConditionalRules(objectSchema).map((rule) => {
      if (rule.triggerFieldName !== name) {
        return null;
      }

      const triggerFieldPath = prefix ? `${prefix}.${name}` : name;
      const currentFieldValue = getNestedValueByPath(formData, triggerFieldPath);
      const isShown = matchesConditionalShowIf(currentFieldValue, rule.showIf);

      if (!isShown) {
        return null;
      }

      // Fields flagged `x-full-width` break out of the row-group grid entirely and render as their
      // own row below it, matching the edit-mode breakout in form-builder-grouped-fields.tsx.
      const inlineDependentEntries = sortJsonFormFields(
        rule.dependentFieldNames.flatMap((dependentFieldName) => {
          const dependentField = objectSchema.properties[dependentFieldName];

          return dependentField && !isFullWidthField(dependentField)
            ? [[dependentFieldName, dependentField] as [string, JsonFormSchema]]
            : [];
        }),
      );

      const dependentNodes = compactNodes(
        renderGroupedPropertyEntries(
          objectSchema,
          prefix,
          inlineDependentEntries,
          ([dependentFieldName, dependentField]) =>
            renderField(dependentFieldName, dependentField, objectSchema, prefix),
        ),
      );

      const fullWidthDependentNodes = compactNodes(
        rule.dependentFieldNames.flatMap((dependentFieldName) => {
          const dependentField = objectSchema.properties[dependentFieldName];

          return dependentField && isFullWidthField(dependentField)
            ? [renderField(dependentFieldName, dependentField, objectSchema, prefix)]
            : [];
        }),
      );

      if (dependentNodes.length === 0 && fullWidthDependentNodes.length === 0) {
        return null;
      }

      return (
        <div
          key={`${triggerFieldPath}-${String(rule.showIf)}`}
          className="flex flex-col gap-4"
        >
          {dependentNodes}
          {fullWidthDependentNodes}
        </div>
      );
    });

  const renderStandaloneField = (
    name: string,
    field: JsonFormSchema,
    parentSchema: JsonFormObjectSchema,
    prefix = "",
  ) => {
    const fieldPath = prefix ? `${prefix}.${name}` : name;

    if (isActionButtonField(field)) {
      return (
        <FormBuilderActionButton
          key={fieldPath}
          name={name}
          field={field}
          actions={actions}
        />
      );
    }

    const label = getFieldTitle(name, field);
    const value = getNestedValueByPath(formData, fieldPath);

    if (!hasFieldDisplayContent(field, value)) {
      return null;
    }

    const conditionalNodes = compactNodes(renderConditionalFields(name, parentSchema, prefix));

    return (
      <div key={fieldPath} className="flex w-full max-w-[600px] flex-col gap-4">
        <FormBuilderFieldDisplay
          label={label}
          value={formatFieldDisplayValueForReadOnly(field, value)}
          href={isDisplayedAsLink(field) ? String(value) : undefined}
        />
        {conditionalNodes}
      </div>
    );
  };

  const renderKeyValueTable = (entries: [string, JsonFormSchema][], prefix: string) => {
    const labeledEntries = entries.filter(
      ([name, field]) =>
        hasFieldDisplayLabel(name, field) &&
        hasFieldDisplayContent(field, getNestedValueByPath(formData, prefix ? `${prefix}.${name}` : name)),
    );

    if (labeledEntries.length === 0) {
      return null;
    }

    const rows = labeledEntries.map(([name, field]) => {
      const fieldPath = prefix ? `${prefix}.${name}` : name;
      const value = getNestedValueByPath(formData, fieldPath);

      return {
        id: fieldPath,
        label: getFieldTitle(name, field),
        value: formatFieldDisplayValueForReadOnly(field, value),
      };
    });

    const tableKey = `${prefix || "root"}-key-value-table-${labeledEntries.map(([name]) => name).join("-")}`;

    return <FormBuilderKeyValueTable rows={rows} key={tableKey} />;
  };

  const flushTableBatch = (
    tableBatch: [string, JsonFormSchema][],
    parentSchema: JsonFormObjectSchema,
    prefix: string,
    nodes: ReactNode[],
  ) => {
    if (tableBatch.length === 0) {
      return;
    }

    if (tableBatch.length >= 2) {
      const keyValueTable = renderKeyValueTable(tableBatch, prefix);

      if (keyValueTable) {
        nodes.push(keyValueTable);
      }
    } else {
      const [scalarName, scalarField] = tableBatch[0];
      const standaloneField = renderStandaloneField(scalarName, scalarField, parentSchema, prefix);

      if (standaloneField) {
        nodes.push(standaloneField);
      }
    }

    tableBatch.length = 0;
  };

  const renderFieldEntries = (
    entries: [string, JsonFormSchema][],
    parentSchema: JsonFormObjectSchema,
    prefix = "",
  ): ReactNode[] => {
    const rowGroups = getRowGroups(parentSchema);
    const entryNames = new Set(entries.map(([entryName]) => entryName));
    const renderedFieldNames = new Set<string>();
    const nodes: ReactNode[] = [];
    const tableBatch: [string, JsonFormSchema][] = [];

    for (const [name, field] of entries) {
      if (renderedFieldNames.has(name)) {
        continue;
      }

      const rowGroup = rowGroups.find(
        (group) =>
          group[0] === name && group.every((propertyName) => entryNames.has(propertyName)),
      );

      if (rowGroup) {
        rowGroup.forEach((propertyName) => renderedFieldNames.add(propertyName));

        const groupEntries = rowGroup.flatMap((propertyName) => {
          const groupField = parentSchema.properties[propertyName];

          return groupField ? [[propertyName, groupField] as [string, JsonFormSchema]] : [];
        });

        if (!useKeyValueTableForScalars) {
          flushTableBatch(tableBatch, parentSchema, prefix, nodes);

          for (const [groupName, groupField] of groupEntries) {
            const fieldNode = renderField(groupName, groupField, parentSchema, prefix);

            if (fieldNode) {
              nodes.push(fieldNode);
            }
          }

          continue;
        }

        const tableEntries = getRowGroupTableEntries(groupEntries, parentSchema);
        const nonTableEntries = groupEntries.filter(
          ([groupName]) => !tableEntries.some(([tableName]) => tableName === groupName),
        );

        if (nonTableEntries.length > 0) {
          flushTableBatch(tableBatch, parentSchema, prefix, nodes);

          for (const [groupName, groupField] of nonTableEntries) {
            const fieldNode = renderField(groupName, groupField, parentSchema, prefix);

            if (fieldNode) {
              nodes.push(fieldNode);
            }
          }
        }

        if (tableEntries.length >= 2) {
          tableBatch.push(...tableEntries);
        } else {
          flushTableBatch(tableBatch, parentSchema, prefix, nodes);

          if (tableEntries.length === 1) {
            const [scalarName, scalarField] = tableEntries[0];
            const standaloneField = renderStandaloneField(
              scalarName,
              scalarField,
              parentSchema,
              prefix,
            );

            if (standaloneField) {
              nodes.push(standaloneField);
            }
          }
        }

        continue;
      }

      flushTableBatch(tableBatch, parentSchema, prefix, nodes);
      renderedFieldNames.add(name);

      if (
        useKeyValueTableForScalars &&
        isKeyValueTableField(field) &&
        (!hasFieldDisplayLabel(name, field) || isConditionalTriggerField(name, parentSchema))
      ) {
        const standaloneField = renderStandaloneField(name, field, parentSchema, prefix);

        if (standaloneField) {
          nodes.push(standaloneField);
        }

        continue;
      }

      const fieldNode = renderField(name, field, parentSchema, prefix);

      if (fieldNode) {
        nodes.push(fieldNode);
      }
    }

    flushTableBatch(tableBatch, parentSchema, prefix, nodes);

    return nodes;
  };

  const renderField = (
    name: string,
    field: JsonFormSchema,
    parentSchema: JsonFormObjectSchema,
    prefix = "",
  ): ReactNode => {
    const fieldPath = prefix ? `${prefix}.${name}` : name;
    const fieldName = fieldPath;
    const label = getFieldTitle(name, field);
    const value = getNestedValueByPath(formData, fieldPath);

    if (isActionButtonField(field)) {
      return (
        <FormBuilderActionButton
          key={fieldName}
          name={name}
          field={field}
          actions={actions}
        />
      );
    }

    if (isObjectSchema(field)) {
      const headingLevel = getObjectHeadingLevel(field);
      const showObjectHeading = label.trim().length > 0;

      const childNodes = compactNodes(
        renderFieldEntries(
          sortJsonFormFields(Object.entries(field.properties)).filter(
            ([propertyName]) =>
              !getConditionalDependentFieldNames(field).has(propertyName),
          ),
          field,
          fieldPath,
        ),
      );

      if (childNodes.length === 0) {
        return null;
      }

      return (
        <div
          key={fieldName}
          className={twMerge(
            "flex flex-col",
            headingLevel === "section" ? "gap-4" : "gap-3",
          )}
        >
          {showObjectHeading && renderSectionHeading(label, headingLevel)}
          <div className="flex max-w-[600px] flex-col gap-3">{childNodes}</div>
        </div>
      );
    }

    if (isDynamicSectionField(field) && field.items?.properties) {
      const rows = Array.isArray(value)
        ? value.filter((item): item is Record<string, unknown> => isRecord(item))
        : [];

      const itemSchema = field.items as JsonFormObjectSchema;
      const headerFieldNames = getDynamicSectionHeaderFieldNames(field);
      const rowsWithContent = rows.filter((row) =>
        Object.entries(itemSchema.properties).some(
          ([propertyName, propertyField]) =>
            !headerFieldNames.has(propertyName) &&
            hasFieldDisplayContent(propertyField, row[propertyName]),
        ),
      );

      if (rowsWithContent.length === 0) {
        const emptyMessage = getDynamicSectionConfig(field).emptyMessage;

        if (emptyMessage) {
          return (
            <p key={fieldName} className="text-base font-semibold leading-6 text-gray-600">
              {emptyMessage}
            </p>
          );
        }

        return null;
      }

      const headingLevel = field["x-heading-level"] ?? "subsection";
      const readOnlyLayout = getDynamicSectionConfig(field).readOnlyLayout;
      const preferFlat =
        readOnlyLayout === "auto" && isFlatDynamicSectionItemSchema(itemSchema);

      if (preferFlat) {
        return (
          <FormBuilderViewerFlatDynamicSection
            key={fieldName}
            title={label}
            headingLevel={headingLevel}
            itemSchema={itemSchema}
            rows={rowsWithContent}
          />
        );
      }

      return (
        <FormBuilderViewerDynamicSection
          key={fieldName}
          title={label}
          headingLevel={headingLevel}
          field={field}
          itemSchema={itemSchema}
          rows={rowsWithContent}
        />
      );
    }

    if (isUploadField(field)) {
      if (!hasFieldDisplayContent(field, value)) {
        return null;
      }

      const config = getUploadConfig(field);

      return (
        <div key={fieldName} className="flex flex-col gap-1">
          <span className="text-sm font-semibold leading-5 text-gray-600">{label}</span>
          <FormBuilderUploadPreview
            src={String(value)}
            alt={label}
            background={config.previewBackground}
          />
        </div>
      );
    }

    return renderStandaloneField(name, field, parentSchema, prefix);
  };

  const renderFields = (schema: JsonFormObjectSchema) => {
    const dependentFieldNames = getConditionalDependentFieldNames(schema);
    const entries = sortJsonFormFields(Object.entries(schema.properties)).filter(
      ([name]) => !dependentFieldNames.has(name),
    );
    useKeyValueTableForScalars = getSectionLayout(schema) !== "compact";

    const nodes = compactNodes(renderFieldEntries(entries, schema, ""));

    if (nodes.length === 0) {
      return shouldShowReadOnlyEmptyState(schema) ? <FormBuilderViewerEmptyState /> : null;
    }

    return <div className="flex flex-col gap-4">{nodes}</div>;
  };

  return { renderFields };
};
